import { useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Block, ErrorNote, Eyebrow, Loading, Screen } from '../components/primitives';
import { ScoreBreakdown } from '../components/panels/score';
import { coaching } from '../lib/api';
import { longDay, shortDay } from '../lib/format';
import { qk } from '../lib/queries';
import { scoreTip } from '../lib/score';

/** SC-36. */
export function ScorePage() {
  const score = useQuery({ queryKey: qk.score, queryFn: coaching.score });
  useEffect(() => {
    coaching.event('success_score_viewed');
  }, []);

  if (score.isPending) return <Loading />;
  if (score.isError) return <ErrorNote error={score.error} retry={() => void score.refetch()} />;
  const s = score.data;
  const tip = scoreTip(s.score);

  return (
    <Screen
      back="/achieve"
      eyebrow="Success Score"
      title={`${s.score.total}`}
      subtitle={s.score.label}
    >
      <ScoreBreakdown score={s.score} />
      {tip && <Block eyebrow="How to raise your score">{tip}</Block>}
      <section className="border-b border-border px-5 py-6">
        <Eyebrow className="mb-4">Last 7 days</Eyebrow>
        <div className="flex h-28 items-end gap-2">
          {s.history.map((h) => (
            <div key={h.day} className="flex flex-1 flex-col items-center gap-2">
              <div
                className="w-full bg-verde"
                style={{ height: `${Math.max(2, (h.score / 100) * 88)}px` }}
                title={`${h.score}`}
              />
              <span className="font-mono text-[0.55rem] uppercase text-sage">
                {shortDay(h.day).slice(0, 2)}
              </span>
            </div>
          ))}
        </div>
      </section>
      <section className="grid grid-cols-2 gap-4 border-b border-border px-5 py-6">
        <div>
          <Eyebrow>Current streak</Eyebrow>
          <p className="mt-1 font-display text-3xl text-ink">{s.streak.current} days</p>
        </div>
        <div>
          <Eyebrow>Longest streak</Eyebrow>
          <p className="mt-1 font-display text-3xl text-ink">{s.streak.longest} days</p>
        </div>
        {s.streak.freeze?.usedOn && (
          <p className="eyebrow col-span-2">
            A streak freeze covered {longDay(s.streak.freeze.usedOn)}. You get one each month.
          </p>
        )}
      </section>
      <p className="px-5 py-6 text-xs leading-relaxed text-muted-foreground">
        The Success Score measures what you do inside the app each day. It does not predict any
        outcome.
      </p>
    </Screen>
  );
}
